import { AfterContentInit, ChangeDetectorRef, Component, computed, NgZone, signal, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DiscoverTileViewComponent } from '@common/components/discover-tile-view/discover-tile-view';
import { TileViewComponent } from '@common/components/tile-view/tile-view';
import { PSTFieldNames } from '@common/data-models/fields/pst-field-names';
import { SearchViewStateRecord } from '@common/data-models/records/search-viewstate-record';
import { SingleViewStateRecord } from '@common/data-models/records/single-viewstate-record';
import { ViewStateLayouts } from '@common/enums/viewstate-layouts';
import { ViewStateTypes } from '@common/enums/viewstate-types';
import { AuthService } from '@common/services/authentication/auth.service';
import { GlobalTablesService } from '@common/services/global-tables-service';
import { LayoutState, ViewStateRecordsAlias } from '@common/services/layout-state';
import { selectCurrentGroup, selectCurrentItem, selectCurrentSection } from '@common/store/navigation/selectors/navigation.selectors';
import {
    selectCurrentGroupBy,
    selectCurrentLayout,
    selectCurrentSortBy,
    selectCurrentViewState,
    selectIsCardLayout,
    selectIsCastCardLayout,
    selectIsCondenseLayout,
    selectIsMapLayout,
    selectIsTileLayout,
    selectIsTrendsLayout,
    selectIsWireLayout,
} from '@common/store/navigation/selectors/viewstate.selectors';
import { LoaderComponent } from '@loader/loader';
import { Store } from '@ngrx/store';
import { VisualStateService } from '@src/suite/common/services/visual-state-service';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { selectListIsLoading } from '@src/suite/common/store/list/list.selectors';
import { ViewStateActions } from '@src/suite/common/store/navigation/actions/viewstate.actions';
import { WireRecord } from '@src/suite/discover/data-models/records/wire-record';
import { map, Observable } from 'rxjs';

import { EnpsRouterComponent } from './enps-router-component';

@Component({
    template: '',
})
export class ListComponent extends EnpsRouterComponent implements AfterContentInit {
    inSearchMode: boolean = false;
    inTagsSearchMode: boolean = false;
    inUsersSearchMode: boolean = false;
    searchTerm: string = '';
    searchTermUser: string = '';
    viewStateLayouts = ViewStateLayouts;

    @ViewChild('tileView') public tileView: TileViewComponent;
    @ViewChild('discoverTileView') public discoverTileView: DiscoverTileViewComponent<WireRecord>;
    @ViewChild('mainCta', { read: TemplateRef, static: true }) mainCta;
    @ViewChild('secondaryCta', { read: TemplateRef, static: true }) secondaryCta;

    @ViewChild('viewOptions', { read: TemplateRef, static: true }) viewOptions;
    @ViewChild('rightSlot', { read: TemplateRef, static: true }) rightSlot;

    loading = signal(false);

    viewStateNavigationItem$ = this.store.select(selectCurrentViewState);
    _vs = computed(() => this.store.selectSignal(selectCurrentViewState)());
    _group = computed(() => this.store.selectSignal(selectCurrentGroup)());
    _section = computed(() => this.store.selectSignal(selectCurrentSection)());
    _item = computed(() => this.store.selectSignal(selectCurrentItem)());
    isLoading = computed(() => this.store.selectSignal(selectListIsLoading)() || this.loading());
    languageSettings = this.store.selectSignal(selectI18N)();

    isCardLayout$ = this.store.select(selectIsCardLayout);
    isCondenseLayout$ = this.store.select(selectIsCondenseLayout);
    isCastCardLayout$ = this.store.select(selectIsCastCardLayout);
    isTileLayout$ = this.store.select(selectIsTileLayout);
    isMapLayout$ = this.store.select(selectIsMapLayout);
    isTrendsLayout$ = this.store.select(selectIsTrendsLayout);
    isWireLayout$ = this.store.select(selectIsWireLayout);

    viewStateLayoutValue$ = this.store.select(selectCurrentLayout);
    viewStateGroupByValue$ = this.store.select(selectCurrentGroupBy);
    viewStateSortByValue$ = this.store.select(selectCurrentSortBy);

    constructor(
        authService: AuthService,
        activatedRoute: ActivatedRoute,
        public globalTablesService: GlobalTablesService,
        public ngZone: NgZone,
        public changeDetectorRef: ChangeDetectorRef,
        public viewStateService: LayoutState,
        public visualStateService: VisualStateService,
        protected store: Store
    ) {
        super(authService, activatedRoute);
    }

    ngAfterContentInit(): void {
        this.store.dispatch(ViewStateActions.updateTemplateRefs({ mainCta: this.mainCta, secondaryCta: this.secondaryCta, viewOptions: this.viewOptions, rightSlot: this.rightSlot }));
    }

    public async onViewStateRecordChange(vs: ViewStateRecordsAlias) {
        if (vs.viewStateType === ViewStateTypes.SEARCH || vs.viewStateType === ViewStateTypes.WIRESEARCH || vs.viewStateType === ViewStateTypes.DRAFTSEARCH) {
            this.inSearchMode = true;

            this.searchTerm = decodeURIComponent((<SearchViewStateRecord>vs).viewStateSearchQuery);
            if (this.searchTerm && this.searchTerm.startsWith('@')) {
                const userID = this.searchTerm.substr(1);
                const staff = this.globalTablesService.Staff[userID];
                if (staff) {
                    this.searchTerm = `@${staff.GivenName} ${staff.Surname}`;
                }
                this.inUsersSearchMode = true;
                this.inTagsSearchMode = false;
            }
            if (this.searchTerm && this.searchTerm.startsWith('@')) {
                this.inUsersSearchMode = false;
                this.inTagsSearchMode = true;
            } else {
                this.inTagsSearchMode = this.inUsersSearchMode = false;
            }
        } else {
            this.inSearchMode = false;
        }
    }

    public showLoader() {
        this.loading.set(true);
    }

    public hideLoader() {
        this.loading.set(false);
    }

    @ViewChild('loader') loader: LoaderComponent;
    public get loaderLabel(): string {
        return this.languageSettings.PSTWebGettingListLoaderLabel;
    }

    get shouldShowLayoutOptions(): boolean {
        return !this.visualStateService.isLessThanSmall;
    }

    public get viewStateSupportsLayout$(): Observable<boolean> {
        return this.viewStateNavigationItem$.pipe(
            map(item => {
                switch (item?.viewStateType) {
                    case ViewStateTypes.TOPIC:
                    case ViewStateTypes.RSSFEED:
                    case ViewStateTypes.TWITTERFEED:
                    case ViewStateTypes.WIREFEED:
                    case ViewStateTypes.MULTIPLEFEED:
                    case ViewStateTypes.APFEED:
                    case ViewStateTypes.FTPFEED:
                    case ViewStateTypes.SCRIPT:
                    case ViewStateTypes.ROLIST:
                        return true;
                    default:
                        return false;
                }
            })
        );
    }

    public get viewStateSupportsGroupBy$(): Observable<boolean> {
        return this.viewStateNavigationItem$.pipe(
            map(item => {
                switch (item?.viewStateType) {
                    case ViewStateTypes.ASSIGNMENT:
                    case ViewStateTypes.TOPIC:
                    case ViewStateTypes.RSSFEED:
                    case ViewStateTypes.TWITTERFEED:
                    case ViewStateTypes.WIREFEED:
                    case ViewStateTypes.MULTIPLEFEED:
                    case ViewStateTypes.APFEED:
                    case ViewStateTypes.FTPFEED:
                    case ViewStateTypes.SCRIPT:
                    case ViewStateTypes.ROLIST:
                    case ViewStateTypes.WIRESEARCH:
                        return true;
                    default:
                        return false;
                }
            })
        );
    }

    get viewStateSupportsGroupByOrder$(): Observable<boolean> {
        return this.viewStateNavigationItem$.pipe(
            map(item => {
                switch (item?.viewStateType) {
                    case ViewStateTypes.ASSIGNMENT:
                    case ViewStateTypes.TOPIC:
                    case ViewStateTypes.RSSFEED:
                    case ViewStateTypes.TWITTERFEED:
                    case ViewStateTypes.WIREFEED:
                    case ViewStateTypes.MULTIPLEFEED:
                    case ViewStateTypes.APFEED:
                    case ViewStateTypes.FTPFEED:
                    case ViewStateTypes.SCRIPT:
                    case ViewStateTypes.ROLIST:
                    case ViewStateTypes.WIRESEARCH:
                        return true;
                    default:
                        return false;
                }
            })
        );
    }

    get viewStateSupportsSortBy$(): Observable<boolean> {
        return this.viewStateNavigationItem$.pipe(
            map(item => {
                switch (item?.viewStateType) {
                    case ViewStateTypes.ASSIGNMENT:
                    case ViewStateTypes.TOPIC:
                    case ViewStateTypes.WIRE:
                    case ViewStateTypes.SEARCH:
                    case ViewStateTypes.RSSFEED:
                    case ViewStateTypes.TWITTERFEED:
                    case ViewStateTypes.WIREFEED:
                    case ViewStateTypes.MULTIPLEFEED:
                    case ViewStateTypes.APFEED:
                    case ViewStateTypes.FTPFEED:
                    case ViewStateTypes.SCRIPT:
                    case ViewStateTypes.ROLIST:
                    case ViewStateTypes.WIRESEARCH:
                        return true;
                    default:
                        return false;
                }
            })
        );
    }

    get viewStateSupportsSortByOrder$(): Observable<boolean> {
        return this.viewStateNavigationItem$.pipe(
            map(item => {
                switch (item?.viewStateType) {
                    case ViewStateTypes.ASSIGNMENT:
                    case ViewStateTypes.TOPIC:
                    case ViewStateTypes.WIRE:
                    case ViewStateTypes.SEARCH:
                    case ViewStateTypes.RSSFEED:
                    case ViewStateTypes.TWITTERFEED:
                    case ViewStateTypes.WIREFEED:
                    case ViewStateTypes.MULTIPLEFEED:
                    case ViewStateTypes.APFEED:
                    case ViewStateTypes.FTPFEED:
                    case ViewStateTypes.SCRIPT:
                    case ViewStateTypes.ROLIST:
                    case ViewStateTypes.WIRESEARCH:
                        return true;
                    default:
                        return false;
                }
            })
        );
    }

    get viewStateGroupByOrderDisabled$(): Observable<boolean> {
        return this.viewStateNavigationItem$.pipe(
            map(item => {
                if (
                    item instanceof SingleViewStateRecord &&
                    (item.viewStateLayout?.Value === ViewStateLayouts.MAP || item.viewStateLayout?.Value === ViewStateLayouts.TILE || item.viewStateSorting?.Value === PSTFieldNames.PSTTopicOrder)
                ) {
                    return true;
                }
                return false;
            })
        );
    }

    get groupByDisabled$(): Observable<boolean> {
        return this.viewStateNavigationItem$.pipe(
            map(item => {
                if (
                    item instanceof SingleViewStateRecord &&
                    (item.viewStateLayout?.Value === ViewStateLayouts.MAP || item.viewStateType === ViewStateTypes.SEARCH || item.viewStateSorting?.Value === PSTFieldNames.PSTTopicOrder)
                ) {
                    return true;
                }
                return false;
            })
        );
    }

    get sortByDisabled$(): Observable<boolean> {
        return this.viewStateNavigationItem$.pipe(
            map(item => {
                if (item instanceof SingleViewStateRecord && (item.viewStateLayout?.Value === ViewStateLayouts.MAP || item.viewStateSorting?.Value === PSTFieldNames.PSTTopicOrder)) {
                    return true;
                }
                return false;
            })
        );
    }

    get viewStateSortDisabled$(): Observable<boolean> {
        return this.viewStateNavigationItem$.pipe(
            map(item => {
                if (item instanceof SingleViewStateRecord && (item.viewStateLayout?.Value === ViewStateLayouts.MAP || item.viewStateSorting?.Value === PSTFieldNames.PSTTopicOrder)) {
                    return true;
                }
                return false;
            })
        );
    }

    get viewStateSortingManual$(): Observable<boolean> {
        return this.viewStateNavigationItem$.pipe(
            map(item => {
                if (item instanceof SingleViewStateRecord && item.viewStateSorting?.Value === PSTFieldNames.PSTTopicOrder) {
                    return true;
                }
                return false;
            })
        );
    }
}
